import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

/* ─── DATA ─────────────────────────────────────────────────── */

const yapisalRows = [
  {
    label: "Gövde",
    aksess: "1,5 mm Sac + 120 mm Mineral Yün + 1,5 mm Sac",
    standard: "2 mm Sac, Kaynaklı Tek Parça",
    aksessNote: null,
    standardNote: null,
  },
  {
    label: "Dış Yalıtım",
    aksess: "Mineral Taşyünü (120 mm)",
    standard: "İnce PÜ / PIR / Taşyünü (30-50 mm)",
    aksessNote: "Yüksek Yalıtım Dayanımı",
    standardNote: "Düşük Yalıtım Dayanımı",
  },
  {
    label: "İç Yalıtım",
    aksess: "120 mm Mineral Yün + Magnezyum Oksit plaka",
    standard: "Yok",
    aksessNote: null,
    standardNote: null,
  },
  {
    label: "İç Kaplama",
    aksess: "18 mm Magnezyum Oksit plaka",
    standard: "Boya / Galvaniz",
    aksessNote: null,
    standardNote: null,
  },
  {
    label: "Modülerlik",
    aksess: "Modüler prefabrike panel yapı",
    standard: "Yekpare tek parça",
    aksessNote: "(Büyütülebilir / genişletilebilir)",
    standardNote: "(Büyütülemez / bölünemez)",
  },
  {
    label: "Taşınabilirlik",
    aksess: "Parça yapısı ile basit taşıma",
    standard: "35 - 40 ton yekpare yapı (Özel proje taşımacılığı gerekli)",
    aksessNote: null,
    standardNote: null,
  },
];

const elektrikRows = [
  {
    label: "Yerleşim",
    aksess: "Ayrı Odalar (Batarya / PCS / HVAC / SCADA)",
    standard: "Sabit modüller, sınırlı erişim",
    aksessNote: null,
    standardNote: null,
  },
  {
    label: "Bakım",
    aksess: "İç servis yolu ile bölmelere rahat erişilebilir",
    standard: "Servis yolu ve bakım boşluğu yok",
    aksessNote: "Kolay ve Rahat Bakım",
    standardNote: "Zor Bakım",
  },
  {
    label: "Kablo Yönetimi",
    aksess: "Gömülü ve kanal sistemli",
    standard: "Modüllere tutturulmuş",
    aksessNote: "Düzenli Kablo Geçişleri",
    standardNote: "Yoğun Kablo Geçişleri",
  },
];

const yanginRows = [
  {
    label: "Yangın Dayanımı",
    aksess: "EI120 üzeri\n(2 saatten fazla)",
    standard: "EI60-120\n(1-2 saat arası)",
    aksessNote: null,
    standardNote: null,
  },
  {
    label: "İç Yüzey",
    aksess: "Magnezyum Oksit (A1 - yanmaz) levha kaplama",
    standard: "Sac yüzey",
    aksessNote: "Yüksek Yangın Dayanımı",
    standardNote: "Düşük Yangın Dayanımı",
  },
  {
    label: "Isı Transferi",
    aksess: "Mineral taş yünü çekirdek ile",
    standard: "Sac levha ile",
    aksessNote: "Düşük Isı Transferi",
    standardNote: "Hızlı Isı Transferi",
  },
  {
    label: "Yangın Bölmeleme",
    aksess: "Bağımsız Batarya Odaları ile",
    standard: "Tüm bataryalar tek hacimde",
    aksessNote: "Yangın Bölmeleme",
    standardNote: "Bölmelenemez Yangın Riski",
  },
  {
    label: "Soğutma Sistemi",
    aksess: "Hava & Sıvı & Gaz Soğutmalı (Hibrit)",
    standard: "Sıvı Soğutmalı, yangına dayanımsız",
    aksessNote: "Çelik Boru ile",
    standardNote: "Plastik Borular ile",
  },
  {
    label: "HVAC - İklimlendirme",
    aksess: "Merkezi Chiller Isıtma / Soğutma ile\nHava + Sıvı Hibrit Isıtma / Soğutma ile",
    standard: "Konteyner bazlı iklimlendirme ile\nSıvı Isıtma / Soğutma ile",
    aksessNote: "Enerji Verimli İklimlendirme\nHomojen İklimlendirme",
    standardNote: "Yüksek Enerji Tüketimi\nDüzensiz İklimlendirme",
  },
];

/* ─── SUB-COMPONENTS ────────────────────────────────────────── */

interface TableSectionProps {
  title: string;
  rows: {
    label: string;
    aksess: string;
    standard: string;
    aksessNote: string | null;
    standardNote: string | null;
  }[];
}

function TableSection({ title, rows }: TableSectionProps) {
  return (
    <div className="mb-12">
      {/* Section title row */}
      <div className="grid grid-cols-3 bg-[#2B2B2B]">
        <div className="px-6 py-4">
          <p className="text-xs font-bold text-white uppercase tracking-widest">{title}</p>
        </div>
        <div className="px-6 py-4 border-l border-white/10 flex items-center gap-2">
          <div className="w-1.5 h-1.5 bg-[#A66A3F] rounded-full flex-shrink-0" />
          <p className="text-xs font-bold text-white uppercase tracking-widest">Prefabrike Enerji Depolama Binası</p>
        </div>
        <div className="px-6 py-4 border-l border-white/10">
          <p className="text-xs font-semibold text-white/50 uppercase tracking-widest">Standart 20&quot; Konteyner ESS</p>
        </div>
      </div>

      {/* Data rows */}
      <div className="border border-gray-100 border-t-0">
        {rows.map((row, i) => (
          <div
            key={i}
            className="grid grid-cols-3 border-b border-gray-100 last:border-b-0 hover:bg-[#FAFAFA] transition-colors duration-150"
          >
            {/* Label */}
            <div className="px-6 py-4 border-r border-gray-100 flex items-start">
              <p className="text-sm font-semibold text-[#A66A3F] leading-snug">{row.label}</p>
            </div>
            {/* AKSESS */}
            <div className="px-6 py-4 border-r border-gray-100 border-l-2 border-l-[#A66A3F] bg-white flex flex-col justify-center gap-0.5">
              {row.aksess.split("\n").map((line, j) => (
                <p key={j} className="text-sm text-[#2B2B2B]">{line}</p>
              ))}
              {row.aksessNote && row.aksessNote.split("\n").map((note, j) => (
                <p key={j} className="text-xs font-bold text-[#A66A3F] mt-0.5">{note}</p>
              ))}
            </div>
            {/* Standard */}
            <div className="px-6 py-4 flex flex-col justify-center gap-0.5">
              {row.standard.split("\n").map((line, j) => (
                <p key={j} className="text-sm text-[#6B6B6B]">{line}</p>
              ))}
              {row.standardNote && row.standardNote.split("\n").map((note, j) => (
                <p key={j} className="text-xs font-bold text-[#8A8A8A] mt-0.5">{note}</p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── PAGE ──────────────────────────────────────────────────── */

export default function ComparisonPage() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-[#FAFAFA] border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-xs text-gray-400 hover:text-[#A66A3F] transition-colors cursor-pointer">Anasayfa</Link>
            <span className="text-gray-300 text-xs">/</span>
            <span className="text-xs text-[#A66A3F]">Karşılaştırma</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <span className="text-[#A66A3F] text-xs font-semibold tracking-[0.2em] uppercase">Teknik Analiz</span>
              <h1 className="text-4xl md:text-5xl font-bold text-[#2B2B2B] mt-3 leading-tight">
                Karşılaştırma<br />Tablosu
              </h1>
              <p className="text-[#6B6B6B] text-base mt-4 max-w-xl leading-relaxed">
                Prefabrike Enerji Depolama Binası ile Standart 20&quot; Konteyner ESS arasındaki teknik farkları inceleyin.
              </p>
            </div>
            {/* Product images */}
            <div className="flex items-end gap-6">
              <div className="text-center">
                <div className="w-40 h-28 overflow-hidden border border-gray-100 bg-white flex items-center justify-center">
                  <img
                    src="https://public.readdy.ai/ai/img_res/b6a1f3a1-b281-4122-8bc9-44bfd891318b.png"
                    alt="Prefabrike Enerji Depolama Binası"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <p className="text-[10px] font-bold text-[#A66A3F] mt-2 uppercase tracking-wide">Prefabrike BESS</p>
              </div>
              <div className="text-center">
                <div className="w-40 h-28 overflow-hidden border border-gray-100 bg-white flex items-center justify-center">
                  <img
                    src="https://public.readdy.ai/ai/img_res/127c9dc1cf1f86c85e2b67ec400fcf19.jpg"
                    alt="Standart 20 Konteyner ESS"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <p className="text-[10px] font-semibold text-gray-400 mt-2 uppercase tracking-wide">Standart Konteyner ESS</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tables */}
      <section className="py-16 md:py-24" ref={heroRef}>
        <div className="max-w-[1200px] mx-auto px-6">

          <TableSection title="Yapısal ve Lojistik" rows={yapisalRows} />
          <TableSection title="Elektrik / Mekanik Entegrasyon" rows={elektrikRows} />
          <TableSection title="Yangın Güvenliği ve Isı Dayanımı" rows={yanginRows} />

          {/* Big Metrics */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-0 border border-gray-100">
            {/* Batarya Ömrü */}
            <div className="border-r border-gray-100">
              <div className="bg-[#2B2B2B] px-6 py-3">
                <p className="text-xs font-bold text-white uppercase tracking-widest">Batarya Ömrü</p>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                <div className="px-6 py-6 bg-white border-l-2 border-l-[#A66A3F]">
                  <p className="text-[10px] text-[#6B6B6B] leading-snug mb-2">Homojen iklimlendirme sayesinde</p>
                  <p className="text-sm font-bold text-[#A66A3F] uppercase tracking-wide">DÜŞÜK TERMAL DEGRADASYON</p>
                </div>
                <div className="px-6 py-6">
                  <p className="text-[10px] text-[#6B6B6B] leading-snug mb-2">Düzensiz iklimlendirme sonucu</p>
                  <p className="text-sm font-bold text-[#8A8A8A] uppercase tracking-wide">HIZLI TERMAL DEGRADASYON</p>
                </div>
              </div>
            </div>

            {/* Enerji Tüketimi */}
            <div className="border-r border-gray-100">
              <div className="bg-[#2B2B2B] px-6 py-3">
                <p className="text-xs font-bold text-white uppercase tracking-widest">Enerji Tüketimi</p>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                <div className="px-6 py-6 bg-white border-l-2 border-l-[#A66A3F] flex items-center">
                  <p className="text-3xl font-bold text-[#A66A3F]">8-12 <span className="text-lg">kWh / MW</span></p>
                </div>
                <div className="px-6 py-6 flex items-center">
                  <p className="text-3xl font-bold text-[#8A8A8A]">14-20 <span className="text-lg">kWh / MW</span></p>
                </div>
              </div>
            </div>

            {/* RTE */}
            <div>
              <div className="bg-[#2B2B2B] px-6 py-3">
                <p className="text-xs font-bold text-white uppercase tracking-widest">RTE (Round-Trip Efficiency)</p>
                <p className="text-[10px] text-white/40 mt-0.5">Şarj-Deşarj Verimi</p>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                <div className="px-6 py-6 bg-white border-l-2 border-l-[#A66A3F] flex items-center">
                  <p className="text-4xl font-bold text-[#A66A3F]">%92+</p>
                </div>
                <div className="px-6 py-6 flex items-center">
                  <p className="text-4xl font-bold text-[#8A8A8A]">%86-%90</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-[#2B2B2B] px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-white/60 text-sm mb-1">Teknik detaylar için</p>
              <p className="text-2xl md:text-3xl font-bold text-white">Ürün kataloğumuzu inceleyin</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/iletisim"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line" />
                İletişime Geç
              </Link>
              <Link
                to="/"
                onClick={() => setTimeout(() => {
                  const el = document.getElementById("katalog");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }, 100)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#A66A3F] text-white text-sm font-semibold hover:bg-[#8f5a33] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line" />
                Kataloğu İndir
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
